import { useEffect, useRef, useCallback } from 'react'
import { useHeaderState } from './useHeaderState'

export const useMenuManagement = () => {
  const {
    menu,
    setMenuVisibility,
    toggleMenu,
    setCurrentClick,
    setActiveCategory,
    closeAllMenus,
    handleMenuClick,
    clearAllTimeouts
  } = useHeaderState()

  const TIMEOUT_MS = 4000

  const viewFilesTimerRef = useRef(null)
  const accountTimerRef = useRef(null)
  const kinTimerRef = useRef(null)
  const signInTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (viewFilesTimerRef.current) clearTimeout(viewFilesTimerRef.current)
      if (accountTimerRef.current) clearTimeout(accountTimerRef.current)
      if (kinTimerRef.current) clearTimeout(kinTimerRef.current)
      if (signInTimerRef.current) clearTimeout(signInTimerRef.current)
    }
  }, [])

  const clearMenuTimeouts = useCallback(() => {
    // Clear local refs
    if (viewFilesTimerRef.current) {
      clearTimeout(viewFilesTimerRef.current)
      viewFilesTimerRef.current = null
    }
    if (accountTimerRef.current) {
      clearTimeout(accountTimerRef.current)
      accountTimerRef.current = null
    }
    if (kinTimerRef.current) {
      clearTimeout(kinTimerRef.current)
      kinTimerRef.current = null
    }
    if (signInTimerRef.current) {
      clearTimeout(signInTimerRef.current)
      signInTimerRef.current = null
    }

    // Clear any reducer-managed timeouts to keep state consistent
    clearAllTimeouts()
  }, [clearAllTimeouts])

  // Close menus when clicking outside of menus or their toggle buttons
  useEffect(() => {
    const insideSelectors = [
      '#view-files-button',
      '.view-files-nav-pane',
      '.view-files-dropdown',
      '#create-kin-cat-button',
      '.create-a-kin-category-pane',
      '.create-category-dropdown',
      '#desktop-account-display',
      '.desktop-account-pane',
      '#view-account-nav',
      '#signup-login-button',
      '.mobile-signin-pane',
      '.hamburger-menu',
      '.header-account-display'
    ]

    const isClickInsideKnownMenu = target => {
      try {
        return insideSelectors.some(sel => {
          return target.closest && target.closest(sel)
        })
      } catch (err) {
        return false
      }
    }

    const handleDocumentClick = e => {
      // If no menus are open, nothing to do
      const anyOpen =
        menu.isViewFilesMenuVisible ||
        menu.isAccountMenuVisible ||
        menu.isCreateKinCategoryMenuVisible ||
        menu.isHamburgerMenuVisible ||
        menu.isSignInHamburgerMenuVisible ||
        menu.isMobileTopLevelMenuVisible

      if (!anyOpen) return

      const clickedInside = isClickInsideKnownMenu(e.target)

      if (!clickedInside) {
        // Click was outside known menu areas -> close all menus and clear timers
        clearMenuTimeouts()
        closeAllMenus()
      }
    }

    document.addEventListener('click', handleDocumentClick)
    return () => document.removeEventListener('click', handleDocumentClick)
  }, [menu, closeAllMenus, clearAllTimeouts, clearMenuTimeouts])

  const toggleViewFilesMenuVisibility = () => {
    if (!menu.isViewFilesMenuVisible) {
      clearMenuTimeouts()
      closeAllMenus()
      setMenuVisibility('isViewFilesMenuVisible', true)
      setCurrentClick('selectedViewFiles')

      if (viewFilesTimerRef.current) clearTimeout(viewFilesTimerRef.current)
      viewFilesTimerRef.current = setTimeout(() => {
        setMenuVisibility('isViewFilesMenuVisible', false)
        setCurrentClick('')
        viewFilesTimerRef.current = null
      }, TIMEOUT_MS)

      return
    }

    setMenuVisibility('isViewFilesMenuVisible', false)
    if (viewFilesTimerRef.current) {
      clearTimeout(viewFilesTimerRef.current)
      viewFilesTimerRef.current = null
    }
    setCurrentClick('')
  }

  const toggleAccountMenuVisibility = () => {
    if (!menu.isAccountMenuVisible) {
      clearMenuTimeouts()
      closeAllMenus()
      setMenuVisibility('isAccountMenuVisible', true)
      setCurrentClick('selectedAccount')

      if (accountTimerRef.current) clearTimeout(accountTimerRef.current)
      accountTimerRef.current = setTimeout(() => {
        setMenuVisibility('isAccountMenuVisible', false)
        setCurrentClick('')
        accountTimerRef.current = null
      }, TIMEOUT_MS)

      return
    }

    setMenuVisibility('isAccountMenuVisible', false)
    if (accountTimerRef.current) {
      clearTimeout(accountTimerRef.current)
      accountTimerRef.current = null
    }
    setCurrentClick('')
  }

  const toggleCreateKinCategoryMenuVisibility = () => {
    if (!menu.isCreateKinCategoryMenuVisible) {
      clearMenuTimeouts()
      closeAllMenus()
      setMenuVisibility('isCreateKinCategoryMenuVisible', true)
      setCurrentClick('selectedCreateKinCategory')

      if (kinTimerRef.current) clearTimeout(kinTimerRef.current)
      kinTimerRef.current = setTimeout(() => {
        setMenuVisibility('isCreateKinCategoryMenuVisible', false)
        setCurrentClick('')
        kinTimerRef.current = null
      }, TIMEOUT_MS)

      return
    }

    setMenuVisibility('isCreateKinCategoryMenuVisible', false)
    if (kinTimerRef.current) {
      clearTimeout(kinTimerRef.current)
      kinTimerRef.current = null
    }
    setCurrentClick('')
  }

  const toggleHamburgerMenuVisibility = () => {
    if (!menu.isHamburgerMenuVisible) {
      clearMenuTimeouts()
      closeAllMenus()
    }

    toggleMenu('isHamburgerMenuVisible')
    toggleMenu('isMobileTopLevelMenuVisible')
    setCurrentClick('selectedMobileTopLevel')
  }

  const toggleSignInHamburgerMenuVisibility = () => {
    if (!menu.isSignInHamburgerMenuVisible) {
      clearMenuTimeouts()
      closeAllMenus()
      setMenuVisibility('isSignInHamburgerMenuVisible', true)

      if (signInTimerRef.current) clearTimeout(signInTimerRef.current)
      signInTimerRef.current = setTimeout(() => {
        setMenuVisibility('isSignInHamburgerMenuVisible', false)
        signInTimerRef.current = null
      }, TIMEOUT_MS)
    } else {
      setMenuVisibility('isSignInHamburgerMenuVisible', false)
      if (signInTimerRef.current) {
        clearTimeout(signInTimerRef.current)
        signInTimerRef.current = null
      }
    }

    setCurrentClick('')
  }

  const toggleAccountModal = () => {
    toggleMenu('isAccountModalVisible')
    if (menu.isAccountMenuVisible) {
      setMenuVisibility('isAccountMenuVisible', false)
    }
  }

  const toggleLGModal = () => {
    toggleMenu('isLGModalOpen')
    if (menu.isAccountMenuVisible) {
      setMenuVisibility('isAccountMenuVisible', false)
    }
  }

  const handleBuildYourOwnClick = () => {
    toggleMenu('isSubNavVisible')
  }

  const handleMainCategoryClick = categoryName => {
    setActiveCategory(categoryName)
  }

  return {
    // State
    isViewFilesMenuVisible: menu.isViewFilesMenuVisible,
    isHamburgerMenuVisible: menu.isHamburgerMenuVisible,
    isAccountMenuVisible: menu.isAccountMenuVisible,
    currentHeaderClick: menu.currentHeaderClick,
    isMobileTopLevelMenuVisible: menu.isMobileTopLevelMenuVisible,
    isSignInHamburgerMenuVisible: menu.isSignInHamburgerMenuVisible,
    isCreateKinCategoryMenuVisible: menu.isCreateKinCategoryMenuVisible,
    activeMainCategory: menu.activeMainCategory,
    isSubNavVisible: menu.isSubNavVisible,
    isAccountModalVisible: menu.isAccountModalVisible,
    isLGModalOpen: menu.isLGModalOpen,

    // Toggle functions
    toggleViewFilesMenuVisibility,
    toggleAccountMenuVisibility,
    toggleCreateKinCategoryMenuVisibility,
    toggleHamburgerMenuVisibility,
    toggleSignInHamburgerMenuVisibility,
    toggleAccountModal,
    toggleLGModal,
    handleBuildYourOwnClick,
    handleMainCategoryClick,
    closeAllMenus,
    handleMenuClick,

    // Setters for direct state control
    setIsViewFilesMenuVisible: value =>
      setMenuVisibility('isViewFilesMenuVisible', value),
    setIsAccountMenuVisible: value =>
      setMenuVisibility('isAccountMenuVisible', value),
    setCurrentHeaderClick: setCurrentClick,
    setActiveMainCategory: setActiveCategory,
    setIsSubNavVisible: value => setMenuVisibility('isSubNavVisible', value)
  }
}
